import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { useCallback, useEffect, useMemo } from 'react';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { useSubtotals } from '../../context/SubtotalsProvider';
import I18n from 'I18n';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedList from 'I18n/components/FormattedList';
import { useDispatch, useSelector } from 'react-redux';
import { getShowAllErrors, setErrorMessages } from '../../state/errorMessagesSlice';
import { useGetErrorMessagesAndTranslations } from '../../hooks/useGetErrorMessagesAndTranslations';
import { RECURRING_BILLING_FREQUENCIES } from '../../constants/RecurringBillingFrequencies';
import { getRecurringBillingFrequency } from 'payments-checkout-lib/utils/recurring';
import { jsx as _jsx } from "react/jsx-runtime";
export default function ClickToCancelCheckbox() {
  var _checkoutSession$user;
  const {
    formState,
    dispatch,
    checkoutSession
  } = usePaymentFormContext();
  const reduxDispatch = useDispatch();
  const {
    recurringSubtotals = []
  } = useSubtotals();
  const clickToCancelAcknowledged = formState.get('clickToCancelAcknowledged');
  const loading = formState.get('isCheckoutSubmitPending');
  const recurringSubtotal = recurringSubtotals[0];
  const recurringBillingTerms = recurringSubtotal === null || recurringSubtotal === void 0 ? void 0 : recurringSubtotal.recurringBillingTerms;
  const numPayments = recurringBillingTerms === null || recurringBillingTerms === void 0 ? void 0 : recurringBillingTerms.numPayments;
  const total = recurringSubtotal === null || recurringSubtotal === void 0 ? void 0 : recurringSubtotal.total;
  const endsAtTimestamp = recurringBillingTerms === null || recurringBillingTerms === void 0 ? void 0 : recurringBillingTerms.endsAt;
  const endDate = endsAtTimestamp ? I18n.moment(endsAtTimestamp).format('LL') : null;
  const IntervalTranslations = useMemo(() => ({
    [RECURRING_BILLING_FREQUENCIES.DAYS]: I18n.text('payments.recurringBillingUnit.days'),
    [RECURRING_BILLING_FREQUENCIES.WEEKS]: I18n.text('payments.recurringBillingUnit.weeks'),
    [RECURRING_BILLING_FREQUENCIES.TWO_WEEKS]: I18n.text('payments.recurringBillingUnit.twoWeeks'),
    [RECURRING_BILLING_FREQUENCIES.MONTHS]: I18n.text('payments.recurringBillingUnit.months'),
    [RECURRING_BILLING_FREQUENCIES.QUARTERS]: I18n.text('payments.recurringBillingUnit.quarters'),
    [RECURRING_BILLING_FREQUENCIES.SIX_MONTHS]: I18n.text('payments.recurringBillingUnit.sixMonths'),
    [RECURRING_BILLING_FREQUENCIES.YEARS]: I18n.text('payments.recurringBillingUnit.years'),
    // TWO_YEARS/THREE_YEARS are included for completeness but 2/3-year billing
    // isn't offered yet, so these are not currently reachable in practice.
    [RECURRING_BILLING_FREQUENCIES.TWO_YEARS]: I18n.text('payments.recurringBillingUnit.twoYears'),
    [RECURRING_BILLING_FREQUENCIES.THREE_YEARS]: I18n.text('payments.recurringBillingUnit.threeYears')
  }), []);
  const companyName = ((_checkoutSession$user = checkoutSession.userDefinedBranding) === null || _checkoutSession$user === void 0 ? void 0 : _checkoutSession$user.companyName) || null;
  const currencyCode = checkoutSession.currencyCode;
  const showAllErrors = useSelector(getShowAllErrors);
  const isError = showAllErrors && !clickToCancelAcknowledged;
  useEffect(() => {
    reduxDispatch(setErrorMessages({
      clickToCancelNotAcknowledged: {
        isError
      }
    }));
  }, [isError, reduxDispatch]);
  const errorMessage = useGetErrorMessagesAndTranslations().get('clickToCancelNotAcknowledged');
  const handleAcknowledgementChanged = useCallback(event => {
    dispatch({
      type: 'setClickToCancelAcknowledged',
      data: event.target.checked
    });
  }, [dispatch]);
  const recurringLineItems = useMemo(() => (checkoutSession.lineItems || []).filter(item => item.recurringBillingTerms), [checkoutSession.lineItems]);
  const areAllRecurringLineItemsFixedPayments = useMemo(() => recurringLineItems.every(item => {
    var _item$recurringBillin;
    return (_item$recurringBillin = item.recurringBillingTerms) === null || _item$recurringBillin === void 0 ? void 0 : _item$recurringBillin.numPayments;
  }), [recurringLineItems]);
  const allLineItemsHaveSameNumPayments = useMemo(() => {
    var _checkoutSession$line, _checkoutSession$line2;
    if (!((_checkoutSession$line = checkoutSession.lineItems) !== null && _checkoutSession$line !== void 0 && _checkoutSession$line.length)) {
      return false;
    }
    const firstItemNumPayments = (_checkoutSession$line2 = checkoutSession.lineItems[0]) === null || _checkoutSession$line2 === void 0 || (_checkoutSession$line2 = _checkoutSession$line2.recurringBillingTerms) === null || _checkoutSession$line2 === void 0 ? void 0 : _checkoutSession$line2.numPayments;
    return checkoutSession.lineItems.every(item => {
      var _item$recurringBillin2;
      return ((_item$recurringBillin2 = item.recurringBillingTerms) === null || _item$recurringBillin2 === void 0 ? void 0 : _item$recurringBillin2.numPayments) === firstItemNumPayments;
    });
  }, [checkoutSession.lineItems]);
  const hasMultipleLineItemsWithDifferentCadences = useMemo(() => {
    var _recurringLineItems$;
    if (recurringLineItems.length <= 1) {
      return false;
    }
    const firstCadence = (_recurringLineItems$ = recurringLineItems[0]) === null || _recurringLineItems$ === void 0 ? void 0 : _recurringLineItems$.recurringBillingTerms;
    if (!firstCadence) return false;
    return !recurringLineItems.every(item => {
      const terms = item.recurringBillingTerms;
      return terms && terms.intervalUnit === firstCadence.intervalUnit && terms.intervalLength === firstCadence.intervalLength;
    });
  }, [recurringLineItems]);
  const lineItemCadencePaymentsString = useMemo(() => {
    if (!hasMultipleLineItemsWithDifferentCadences) return '';
    const paymentStrings = recurringLineItems.map(item => {
      const terms = item.recurringBillingTerms;
      const frequency = terms !== null && terms !== void 0 && terms.intervalUnit && terms !== null && terms !== void 0 && terms.intervalLength ? getRecurringBillingFrequency(terms) : null;
      const intervalTranslation = frequency ? IntervalTranslations[frequency] : '';
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "payments.merchantTermsOfService.clickToCancel.intervalPayment_jsx",
        options: {
          value: item.amount,
          interval: intervalTranslation
        },
        elements: {
          Price: props => /*#__PURE__*/_jsx(FormattedIntlCurrency, Object.assign({}, props, {
            currencyCode: currencyCode
          }))
        }
      }, item.id);
    });
    return /*#__PURE__*/_jsx(FormattedList, {
      children: paymentStrings
    });
  }, [recurringLineItems, hasMultipleLineItemsWithDifferentCadences, IntervalTranslations, currencyCode]);
  const allLineItemsHaveSameFixedPayments = useMemo(() => {
    return numPayments && allLineItemsHaveSameNumPayments && areAllRecurringLineItemsFixedPayments;
  }, [numPayments, allLineItemsHaveSameNumPayments, areAllRecurringLineItemsFixedPayments]);
  const checkboxTranslationMessage = useMemo(() => {
    if (
    // Only support different cadences in payment links and quotes
    checkoutSession.sourceApp !== 'INVOICE' && hasMultipleLineItemsWithDifferentCadences) {
      return allLineItemsHaveSameFixedPayments ? 'payments.merchantTermsOfService.clickToCancel.checkboxWithDifferentCadencesNumPayments_jsx' : 'payments.merchantTermsOfService.clickToCancel.checkboxWithDifferentCadencesAutoRenew_jsx';
    } else if (endDate) {
      return 'payments.merchantTermsOfService.clickToCancel.checkboxWithEndDate_jsx';
    } else if (numPayments) {
      return 'payments.merchantTermsOfService.clickToCancel.checkboxWithNumPayments_jsx';
    }
    return 'payments.merchantTermsOfService.clickToCancel.checkboxWithAutoRenew_jsx';
  }, [checkoutSession.sourceApp, hasMultipleLineItemsWithDifferentCadences, allLineItemsHaveSameFixedPayments, endDate, numPayments]);
  const recurringBillingFrequency = recurringBillingTerms !== null && recurringBillingTerms !== void 0 && recurringBillingTerms.intervalUnit && recurringBillingTerms !== null && recurringBillingTerms !== void 0 && recurringBillingTerms.intervalLength ? getRecurringBillingFrequency(recurringBillingTerms) : null;
  return /*#__PURE__*/_jsx(FormControl, {
    "data-test-id": "click-to-cancel-checkbox",
    required: true,
    error: isError,
    validationMessage: isError && errorMessage,
    children: /*#__PURE__*/_jsx(Checkbox, {
      color: "icon-interactive-default",
      size: "small",
      checked: clickToCancelAcknowledged,
      onChange: handleAcknowledgementChanged,
      disabled: loading,
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: checkboxTranslationMessage,
        options: {
          value: total,
          currencyCode,
          interval: recurringBillingFrequency ? IntervalTranslations[recurringBillingFrequency] : '',
          companyName,
          // numPayments is the total number of payments,
          // need to subtract 1 to get the number of payments after current payment
          numPayments: numPayments ? numPayments - 1 : null,
          lineItemPayments: lineItemCadencePaymentsString,
          endDate
        },
        elements: {
          Price: props => /*#__PURE__*/_jsx(FormattedIntlCurrency, Object.assign({}, props, {
            currencyCode: currencyCode
          }))
        }
      })
    })
  });
}